import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import RichHTML from "../components/RichHTML";

const TITLES = {
  impressum: "Impressum",
  datenschutz: "Datenschutz",
  agb: "AGB / Widerruf",
};

export default function Legal({ type }) {
  const [s, setS] = useState({});

  useEffect(() => {
    api.get("/content").then((r) => setS(r.data.settings || {})).catch(() => {});
    window.scrollTo(0, 0);
  }, [type]);

  const heading = TITLES[type] || "Rechtliches";
  const agbEmpty = type === "agb" && !s.agb && !s.agb_pdf && !s.widerruf && !s.widerruf_pdf;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[var(--mf-line)]">
        <div className="mf-container h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
            <img src="/mfmed-logo.png" alt="MF MED GmbH Logo" className="h-9 w-auto" />
            <span className="font-head font-bold tracking-tight text-[var(--mf-ink)]">MF MED GmbH</span>
          </Link>
          <Link to="/" className="mf-cta-ghost text-sm py-2.5 px-4"><ArrowLeft size={16} /> Zur Startseite</Link>
        </div>
      </header>

      <main className="mf-container py-16 lg:py-24 max-w-3xl">
        <span className="mf-eyebrow">Rechtliches</span>
        <h1 className="font-head font-light tracking-tight text-4xl lg:text-5xl mt-6 text-[var(--mf-ink)]" data-testid="legal-title">
          {heading}
        </h1>

        {type === "agb" ? (
          <div className="mt-12 space-y-16">
            <LegalSection id="agb" title="Allgemeine Geschäftsbedingungen (AGB)"
              text={s.agb} pdf={s.agb_pdf} pdfLabel="AGB als PDF öffnen" />
            <LegalSection id="widerruf" title="Widerrufsbelehrung"
              text={s.widerruf} pdf={s.widerruf_pdf} pdfLabel="Widerrufsbelehrung als PDF öffnen" />
            {agbEmpty && (
              <p className="text-[var(--mf-grey)]" data-testid="legal-empty">Die Inhalte werden in Kürze bereitgestellt.</p>
            )}
          </div>
        ) : (
          <div className="mt-10 pl-6 mf-redline leading-relaxed text-[var(--mf-ink-soft)]" data-testid="legal-content">
            <RichHTML html={s[type] || ""} />
          </div>
        )}
      </main>

      <footer className="border-t border-[var(--mf-line)]">
        <div className="mf-container py-6 text-sm text-[var(--mf-grey)] flex flex-wrap gap-4 justify-between">
          <span>© {new Date().getFullYear()} MF MED GmbH</span>
          <span className="flex gap-4">
            <Link to="/impressum" className="hover:text-[var(--mf-ink)]">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[var(--mf-ink)]">Datenschutz</Link>
            <Link to="/agb" className="hover:text-[var(--mf-ink)]">AGB / Widerruf</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

function LegalSection({ id, title, text, pdf, pdfLabel }) {
  if (!text && !pdf) return null;
  return (
    <section data-testid={`legal-section-${id}`}>
      <h2 className="font-head text-2xl font-semibold text-[var(--mf-ink)]">{title}</h2>
      {pdf && (
        <a href={pdf} target="_blank" rel="noopener noreferrer"
          data-testid={`${id}-pdf-download`}
          className="mf-cta text-sm py-2.5 px-4 mt-5 inline-flex items-center gap-2">
          <ExternalLink size={16} /> {pdfLabel}
        </a>
      )}
      {text && (
        <div className="mt-6 pl-6 mf-redline leading-relaxed text-[var(--mf-ink-soft)]" data-testid={`${id}-content`}>
          <RichHTML html={text} />
        </div>
      )}
    </section>
  );
}
